import React, { Suspense, lazy } from "react"
import { Global, css } from "@emotion/react"
import styled from "@emotion/styled/macro"
import { theme, device } from "./Theme"
import {
  useForReviewsWidget,
  GeneralSchemaFunction,
  useHeaderData,
  useFinalTopBarMenuData,
  useMainMenuImages,
  useMainMenuItems,
  useMegaMenuButtonsData,
  useFooterData,
  useContactData,
  useButtonTextData,
} from "./DataImports"
import { GeneralSchema } from "@ashleynexvelsolutions/general-schema"
import { HeaderDesktop } from "./HeaderDesktop"
import { HeaderMobile } from "./HeaderMobile"
import checkmark from "../images/SVGs/check-mark.svg"
import blogCheckmark from "../images/blog-check-mark.jpg"
import ProximaNovaLight from "../fonts/ProximaNova-Light.woff2"
import ProximaNovaBold from "../fonts/ProximaNova-Bold.woff2"
import ProximaNovaRegular from "../fonts/ProximaNova-Regular.woff2"
import ProximaNovaSemiBold from "../fonts/ProximaNova-Semibold.woff2"
import ProximaNovaExtraBold from "../fonts/ProximaNova-Extrabld.woff2"
import ProximaNovaExtraBoldItalic from "../fonts/ProximaNova-ExtrabldIt.woff2"
import ProximaNovaThin from "../fonts/ProximaNova-Thin.woff2"
import ProximaNovaThinItalic from "../fonts/ProximaNova-ThinIt.woff2"
import ProximaNovaBlack from "../fonts/ProximaNova-Black.woff2"
import BitterBold from "../fonts/Bitter-Bold.woff2"
import BitterRegular from "../fonts/Bitter-Regular.woff2"
import BitterBoldItalic from "../fonts/Bitter-BoldItalic.woff2"
import BitterBlack from "../fonts/Bitter-Black.woff2"

//Use only for below the fold stuff.
const Footer = lazy(() =>
  import("./Footer").then(module => ({
    default: module.Footer,
  }))
)

const GlobalStyles = () => {
  return (
    <Global
      styles={css`
        @font-face {
          font-family: "Bitter";
          src: url(${BitterBlack}) format("woff2");
          font-weight: 900;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: "Bitter";
          src: url(${BitterBold}) format("woff2");
          font-weight: 700;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: "Bitter";
          src: url(${BitterBoldItalic}) format("woff2");
          font-weight: 700;
          font-style: italic;
          font-display: swap;
        }
        @font-face {
          font-family: "Bitter";
          src: url(${BitterRegular}) format("woff2");
          font-weight: 400;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: "Proxima Nova";
          src: url(${ProximaNovaBlack}) format("woff2");
          font-weight: 900;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: "Proxima Nova";
          src: url(${ProximaNovaBold}) format("woff2");
          font-weight: 700;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: "Proxima Nova";
          src: url(${ProximaNovaExtraBold}) format("woff2");
          font-weight: 800;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: "Proxima Nova";
          src: url(${ProximaNovaExtraBoldItalic}) format("woff2");
          font-weight: 800;
          font-style: italic;
          font-display: swap;
        }
        @font-face {
          font-family: "Proxima Nova";
          src: url(${ProximaNovaLight}) format("woff2");
          font-weight: 300;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: "Proxima Nova";
          src: url(${ProximaNovaRegular}) format("woff2");
          font-weight: 400;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: "Proxima Nova";
          src: url(${ProximaNovaSemiBold}) format("woff2");
          font-weight: 600;
          font-style: normal;
          font-display: swap;
        }
        @font-face {
          font-family: "Proxima Nova";
          src: url(${ProximaNovaThinItalic}) format("woff2");
          font-weight: 100;
          font-style: italic;
          font-display: swap;
        }
        @font-face {
          font-family: "Proxima Nova";
          src: url(${ProximaNovaThin}) format("woff2");
          font-weight: 100;
          font-style: normal;
          font-display: swap;
        }

        /*CSS Reset */
        html,
        body,
        div,
        span,
        iframe,
        h1,
        h2,
        h3,
        h4,
        h5,
        h6,
        p,
        blockquote,
        a,
        em,
        img,
        strong,
        ol,
        ul,
        li,
        form,
        label,
        article,
        aside,
        footer,
        header,
        nav,
        section {
          margin: 0;
          padding: 0;
          border: 0;
          font-size: 100%;
          font: inherit;
          vertical-align: baseline;
        }
        /* HTML5 display-role reset for older browsers */
        article,
        aside,
        footer,
        header,
        nav,
        section {
          display: block;
        }
        ol,
        ul {
          list-style-position: outside;
        }
        li {
          margin: 1rem 0;
        }
        html,
        body {
          margin: 0;
          font-family: ${theme.fonts.main};
          font-variant: normal;
          font-feature-settings: normal;
          line-height: 1;
          color: ${theme.colors.body};
        }
        a {
          text-decoration: none;
        }

        /* End CSS Reset */
        strong {
          font-weight: 700;
        }
        h1 {
          font-size: clamp(2.1875rem, 1.9296rem + 1.1003vw, 3.25rem);
          font-weight: 800;
          color: ${theme.colors.dark};

          span {
            font-weight: inherit !important;
          }
        }
        h3 {
          font-size: clamp(0.875rem, 0.784rem + 0.3883vw, 1.25rem);
          margin-bottom: 1.75em;
        }
        h2 {
          font-size: clamp(1.1875rem, 1.0813rem + 0.4531vw, 1.625rem);
          font-family: ${theme.fonts.altFont};
          margin: 0.5em 0;
          color: ${theme.colors.primary};
        }
        .pageTemplate h2,
        .postTemplate h2 {
          font-family: ${theme.fonts.altFont};
          font-style: italic;
          font-weight: 700;
          color: ${theme.colors.body};
          letter-spacing: 2px;
        }
        .pageTemplate h2 span[style],
        .postTemplate h2 span[style] {
          font-weight: 700 !important;
        }
        svg,
        img {
          max-width: 100%;
        }
        html,
        body {
          font-size: 16px;
        }
        .pageTemplate,
        .postTemplate {
          p:first-of-type {
            margin-top: 1.9375rem;
          }
          p:last-of-type {
            margin-bottom: 1.9375rem;
          }
          p {
            font-size: clamp(1rem, 0.9242rem + 0.3236vw, 1.3125rem);
            line-height: clamp(1.625rem, 1.5492rem + 0.3236vw, 1.9375rem);
            margin: 0.96875rem 0;
          }
          ul {
            list-style-position: inside;
            list-style-type: none;
            margin-bottom: 2rem;

            @media ${device.lg} {
              column-count: 2;
              li:first-of-type {
                margin: 0 0 1rem 0;
              }
            }
          }
          li {
            display: block;
            background-image: url(${checkmark});
            background-repeat: no-repeat;
            background-position: left center;
            min-height: 1.295rem;
            padding-left: 2rem;
            line-height: 1.5;
            font-weight: 700;
            font-size: clamp(1rem, 0.909rem + 0.3883vw, 1.375rem);
            break-inside: avoid;
          }
        }
        .postTemplate {
          ul {
            @media ${device.lg} {
              column-count: auto;
            }
          }
          li {
            background-image: url(${blogCheckmark});
            font-weight: 400;
            font-size: clamp(1rem, 0.9545rem + 0.1942vw, 1.1875rem);
          }
        }
        button {
          color: inherit;
        }

        .fbc ol {
          list-style-type: none;
          margin: 0 0 1em 0;
          line-height: 1.5;
        }
        .fbc ol span .fbc ol a span,
        .fbc ol .fbc-separator,
        .fbc a {
          color: ${theme.colors.secondary};
        }
        .fbc ol li {
          display: inline-block;
          margin: 2px;
        }
        .fbc ol li a {
          text-decoration: none;
        }

        .weatherWidgetAndMap {
          display: grid;
          align-items: center;
          justify-content: center;
        }
        .weatherWidgetAndMap iframe {
          margin: 0 auto;
        }
        .page-generator-pro-related-links {
          list-style: none;
          margin-left: 0;
        }
        @media (min-width: 992px) {
          .page-generator-pro-related-links {
            column-count: 3;
            column-gap: 10px;
            margin-block-start: 0;
            margin-block-end: 0;
            padding-inline-start: 0;
            margin-top: 0 !important;

            li:first-of-type {
              margin: 0 0 1rem 0;
            }
          }
          .weatherWidgetAndMap {
            grid-template-columns: 25% 70%;
            grid-column-gap: 5%;
          }
        }

        .heading {
          font-weight: 800;
          font-size: clamp(2.125rem, 1.6396rem + 2.0712vw, 4.125rem);
        }
        .subheading {
          font-family: ${theme.fonts.altFont};
          font-weight: 700;
          font-style: italic;
          font-size: clamp(1.125rem, 0.9885rem + 0.5825vw, 1.6875rem);
          margin: 1.3125rem 0 1.125rem 0;
        }
        .customModal .heading {
          font-size: clamp(1.56375rem, 1.1924rem + 1.5845vw, 3.09375rem);
        }
        .customModal .subheading {
          font-size: clamp(0.84375rem, 0.7414rem + 0.4369vw, 1.265625rem);
        }
        .customModal .textContainer {
          margin: 1.46875rem 0 0 0 !important;
        }
        .customModal input,
        .customModal textarea {
          padding: 0.59375rem 2.375rem !important;
        }
        .customModal .textContainer {
          text-align: center;
        }
        .customModal .closeButton {
          appearance: none;
          color: ${theme.colors.dark};
          background-color: transparent;
          border: none;
          font-size: 1.5rem;
          position: absolute;
          right: 1.25rem;
          top: 1.25rem;
        }
        .ReactModal__Overlay.customModalOverlay {
          opacity: 0;
          transition: opacity 500ms ease-in-out;
        }

        .ReactModal__Overlay--after-open.customModalOverlay {
          opacity: 1;
        }

        .ReactModal__Overlay--before-close.customModalOverlay {
          opacity: 0;
        }
      `}
    />
  )
}
const Layout = styled.div``
const LayoutComponent = ({ children }) => {
  const ratingsValues = useForReviewsWidget()
  const header = useHeaderData()
  const topBarMenuItems = useFinalTopBarMenuData()
  const mainMenuItems = useMainMenuItems()
  const mainMenuDropdownImages = useMainMenuImages()
  const megaMenuButtons = useMegaMenuButtonsData()
  const contactData = useContactData()
  const footerData = useFooterData()
  const buttonText = useButtonTextData()

  return (
    <>
      <GlobalStyles />
      <GeneralSchema {...GeneralSchemaFunction()} />
      <Layout>
        <HeaderDesktop
          formData={contactData}
          headerData={header}
          topBarMenuItems={topBarMenuItems}
          mainMenuItems={mainMenuItems}
          dropdownImages={mainMenuDropdownImages}
          ratingsValues={ratingsValues}
          megaMenuButtons={megaMenuButtons}
          buttonText={buttonText}
        />
        <HeaderMobile
          headerData={header}
          mainMenuItems={mainMenuItems}
          formData={contactData}
          buttonText={buttonText}
        />
        {children}
        <Suspense fallback="loading">
          <Footer contactData={contactData} data={footerData} />
        </Suspense>
      </Layout>
    </>
  )
}

export { LayoutComponent as Layout }
